import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronsLeftRight, ChevronLeft, ChevronRight, Images, ArrowRight, Package } from "lucide-react";
import api from "../lib/api";
import GoogleReviews from "./GoogleReviews";
import PackagesShowcaseModal from "./PackagesShowcaseModal";
import { useSiteContent } from "../contexts/SiteContentContext";

const FALLBACK_PAIRS = [
  { id: "interior", label: "Interiér · koberečky", before_url: "/images/gallery/pair1-before.jpg", after_url: "/images/gallery/pair1-after.jpg" },
  { id: "trunk", label: "Zavazadlový prostor", before_url: "/images/gallery/pair2-before.jpg", after_url: "/images/gallery/pair2-after.jpg" },
  { id: "exterior", label: "Exteriér · lak", before_url: "/images/gallery/pair3-before.jpg", after_url: "/images/gallery/pair3-after.jpg" },
  { id: "wheel", label: "Kola · disky", before_url: "/images/gallery/pair4-before.jpg", after_url: "/images/gallery/pair4-after.jpg" },
  { id: "mask", label: "Přední maska", before_url: "/images/gallery/pair5-before.jpg", after_url: "/images/gallery/pair5-after.jpg" },
];

export default function Gallery() {
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(50);
  const [width, setWidth] = useState(0);
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const wrapRef = useRef(null);
  const { text } = useSiteContent();
  const pt = text.packages_teaser || {};

  useEffect(() => {
    api.get("/gallery/pairs").then((r) => {
      if (Array.isArray(r.data?.pairs) && r.data.pairs.length > 0) setPairs(r.data.pairs);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const update = () => setWidth(wrapRef.current?.offsetWidth || 0);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const handleMove = (clientX) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, x)));
  };

  const switchPair = (idx) => {
    setActive((idx + pairs.length) % pairs.length);
    setPos(50);
  };

  const pair = pairs[active] || pairs[0];

  return (
    <section id="galerie" className="relative py-20 md:py-32 tone-gallery overflow-hidden" data-testid="gallery-section">
      <div className="lit-gallery absolute inset-0 pointer-events-none" />
      <div className="relative max-w-[1180px] mx-auto px-[18px] md:px-8">
        <div className="mb-8 md:mb-10">
          <span className="eyebrow text-akcent" data-testid="gallery-eyebrow">Galerie</span>
          <h2 className="t-h1 mt-4" style={{ fontSize: "clamp(37.2px, 6.138vw, 61.38px)", color: "var(--krem-100)" }}>Výsledky, které mluví za vše.</h2>
          <p className="mt-3 max-w-[640px]" style={{ fontSize: "15.64px", color: "var(--text-tlum-tmavy)" }}>Přesuňte táhátko a sami se přesvědčte o kvalitě naší práce.</p>
        </div>

        <div className="flex items-center gap-2 flex-nowrap overflow-x-auto no-scrollbar -mx-[18px] px-[18px] sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible pb-1 mb-5" data-testid="gallery-pair-switcher">
          <button
            onClick={() => switchPair(active - 1)}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:text-akcent transition-colors flex-shrink-0"
            style={{ border: "0.5px solid var(--linka-tmava)", color: "var(--text-tlum-tmavy)" }}
            data-testid="gallery-prev-btn" aria-label="Předchozí"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {pairs.map((p, i) => (
            <button
              key={p.id}
              onClick={() => switchPair(i)}
              className="rounded-full font-medium transition-colors flex-shrink-0 whitespace-nowrap"
              style={{
                border: `0.5px solid ${i === active ? "var(--stribro-text)" : "var(--linka-tmava)"}`,
                color: i === active ? "var(--stribro-text)" : "var(--text-tlum-tmavy)",
                padding: "9.6px 16.8px",
                fontSize: "14.4px",
              }}
              data-testid={`gallery-pair-btn-${p.id}`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => switchPair(active + 1)}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:text-akcent transition-colors flex-shrink-0"
            style={{ border: "0.5px solid var(--linka-tmava)", color: "var(--text-tlum-tmavy)" }}
            data-testid="gallery-next-btn" aria-label="Další"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Slider — o 20 % větší, zaoblené rohy */}
        <motion.div
          ref={wrapRef}
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="relative w-full lg:w-[96%] mx-auto aspect-[4/3] md:aspect-[16/9] overflow-hidden rounded-2xl cursor-ew-resize select-none"
          style={{ border: "0.5px solid var(--linka-tmava)" }}
          onMouseMove={(e) => e.buttons === 1 && handleMove(e.clientX)}
          onClick={(e) => handleMove(e.clientX)}
          onTouchMove={(e) => handleMove(e.touches[0].clientX)}
          data-testid="gallery-slider"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={pair.id}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="absolute inset-0"
            >
              <img src={pair.after_url} alt={`${pair.label} – po`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
              <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
                <img src={pair.before_url} alt={`${pair.label} – před`} className="absolute inset-0 max-w-none h-full object-cover" style={{ width: width || "100%" }} draggable={false} />
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/70 text-krem-100 text-xs tracking-wider">PŘED</div>
          <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-akcent text-uhel-900 text-xs tracking-wider font-semibold">PO</div>
          <div className="absolute top-0 bottom-0" style={{ left: `${pos}%` }}>
            <div className="absolute top-0 bottom-0 w-px bg-krem-100/80"></div>
            <div className="slider-handle">
              <ChevronsLeftRight className="w-5 h-5" />
            </div>
          </div>
        </motion.div>

        <p className="mt-6 text-center" style={{ color: "var(--text-tlum-tmavy)", fontSize: "15.5px" }}>
          <span className="text-akcent">Tip:</span> Táhněte slider doleva a doprava pro porovnání před a po
        </p>

        {/* Výrazné tlačítko do samostatné fotogalerie */}
        <div className="mt-8 flex justify-center">
          <Link to="/fotogalerie" className="btn-accent text-sm md:text-base" data-testid="gallery-full-link-btn">
            <Images className="w-4 h-4" /> Zobrazit celou fotogalerii <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <GoogleReviews />

        {/* Balíčky teaser */}
        <motion.div
          initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative mt-10 rounded-2xl p-7 md:p-9 overflow-hidden"
          style={{ backgroundImage: "radial-gradient(120% 100% at 100% 0%, rgba(201,166,107,0.09), transparent 60%)", backgroundColor: "var(--uhel-900)", border: "0.5px solid var(--linka-tmava)" }}
          data-testid="packages-teaser-section"
        >
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-akcent" style={{ background: "var(--akcent-tlum)" }}>
                <Package className="w-6 h-6" />
              </span>
              <div>
                <h4 className="t-h2 text-krem-100" data-testid="packages-teaser-title">
                  {pt.title || "Prohlédněte si naše výhodné balíčky"}
                </h4>
                <p className="mt-2.5 text-tlum-dark text-sm md:text-base max-w-xl" data-testid="packages-teaser-description">
                  {pt.description || "Pravidelná péče vychází levněji než jednotlivé návštěvy. Sestavili jsme balíčky pro řidiče, kteří chtějí mít auto pořád v perfektní kondici."}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPackagesModal(true)}
              className="btn-graphite-outline text-sm md:text-base whitespace-nowrap flex-shrink-0"
              data-testid="packages-teaser-cta-btn"
            >
              {pt.button_label || "Prohlédnout balíčky"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>

      <PackagesShowcaseModal open={showPackagesModal} onClose={() => setShowPackagesModal(false)} />
    </section>
  );
}
